import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, LogOut, Settings as SettingsIcon, Sparkles } from 'lucide-react';

import { useAppSelector } from '@/store/hooks';
import { useMyTournamentsQuery } from '@/services/endpoints/tournaments';
import { useGetMeQuery } from '@/services/endpoints/auth';
import { useGetMyAvatarsQuery } from '@/services/avatarApi';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { Loader } from '@/components/common/Loader';
import { StatusBadge } from '@/components/tournaments/StatusBadge';
import { formatDateShort } from '@/lib/format';
import { UserHeaderBanner } from '@/components/banner/UserHeaderBanner';
import { CustomizeSheet } from '@/components/banner/CustomizeSheet';

export function Profile() {
  const { t } = useTranslation('general');
  const { signOut } = useLogtoAuth();
  const meFromStore = useAppSelector((s) => s.auth.user);
  const meQuery = useGetMeQuery(undefined, { skip: !!meFromStore });
  const me = meFromStore ?? meQuery.data ?? null;
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const myTournaments = useMyTournamentsQuery({ limit: 5 });
  const myAvatars = useGetMyAvatarsQuery({ page: 1, limit: 6 });
  const avatarItems = myAvatars.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      <UserHeaderBanner
        userId={me?.id}
        avatarUrl={me?.avatarUrl}
        avatarFallback={(me?.firstName ?? me?.username ?? '?').charAt(0)}
        onCustomize={() => setCustomizeOpen(true)}
      >
        <div className="min-w-0 pr-12">
          <h1 className="truncate text-base font-bold text-fg drop-shadow">
            {me?.firstName ?? me?.username ?? 'Player'}
          </h1>
          {me?.email && (
            <p className="truncate text-[11px] text-fg-muted drop-shadow">{me.email}</p>
          )}
        </div>
      </UserHeaderBanner>
      <CustomizeSheet
        userId={me?.id}
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
      />

      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('my_tournaments', { defaultValue: 'My tournaments' })}
          </h2>
          <Link to="/tournaments" className="text-xs text-brand">
            {t('view_all', { defaultValue: 'View all' })}
          </Link>
        </div>

        {myTournaments.isLoading && (
          <div className="flex h-20 items-center justify-center">
            <Loader />
          </div>
        )}

        {!myTournaments.isLoading && !myTournaments.data?.data.length && (
          <div className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted">
            {t('my_tournaments_empty', {
              defaultValue: "You haven't joined any tournaments yet.",
            })}
          </div>
        )}

        <ul className="flex flex-col gap-2">
          {myTournaments.data?.data.map((tournament) => (
            <li key={tournament.id}>
              <Link
                to={`/tournaments/${tournament.id}`}
                className="flex items-center justify-between rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-fg">{tournament.title}</p>
                  <p className="text-xs text-fg-muted">
                    {formatDateShort(tournament.started_at)}
                  </p>
                </div>
                <StatusBadge status={tournament.status} />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('my_avatars', { defaultValue: 'My avatars' })}
          </h2>
          <Link to="/avatars" className="text-xs text-brand">
            {t('view_all', { defaultValue: 'View all' })}
          </Link>
        </div>

        {myAvatars.isLoading ? (
          <div className="flex h-20 items-center justify-center">
            <Loader />
          </div>
        ) : avatarItems.length === 0 ? (
          <Link
            to="/avatars/new"
            className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4 transition-opacity active:opacity-80"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Sparkles aria-hidden className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg">Create your first avatar</p>
              <p className="text-xs text-fg-muted">AI-generated, in seconds.</p>
            </div>
            <ChevronRight className="h-4 w-4 text-fg-muted" />
          </Link>
        ) : (
          <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-1">
            {avatarItems.map((avatar) => (
              <Link
                key={avatar._id}
                to={`/avatars/${avatar._id}`}
                className="flex w-24 shrink-0 flex-col gap-1.5"
              >
                <div className="aspect-square overflow-hidden rounded-xl bg-bg-tertiary">
                  {avatar.thumbnailUrl && (
                    <img
                      src={avatar.thumbnailUrl}
                      alt={avatar.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
                <p className="truncate text-[11px] text-fg-muted">{avatar.title}</p>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-1">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('account', { defaultValue: 'Account' })}
        </h2>

        <ProfileLink to="/settings" icon={SettingsIcon}>
          {t('settings', { defaultValue: 'Settings' })}
        </ProfileLink>

        <button
          type="button"
          onClick={signOut}
          className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
        >
          <LogOut className="h-5 w-5 text-danger" />
          <span className="flex-1 text-sm font-medium text-danger">
            {t('logout', { defaultValue: 'Sign out' })}
          </span>
        </button>
      </section>
    </div>
  );
}

interface ProfileLinkProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
}

function ProfileLink({ to, icon: Icon, children }: ProfileLinkProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
    >
      <Icon className="h-5 w-5 text-fg" />
      <span className="flex-1 text-sm font-medium text-fg">{children}</span>
      <ChevronRight className="h-4 w-4 text-fg-muted" />
    </Link>
  );
}
